// /embed/widget/{dashboard_id}/{widget_id}: one widget with no shell, for the phone apps' web-view widget
// (VisionPlatform specs/mobile-dashboards-2 MD2-03). Signed in with the phone app's token, never by a sign-in of its
// own. Links inside the widget do not navigate: the web view never opens other pages. In a plain browser it only
// says to open it in the app.
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { setCurrentOrgId } from "../org/orgStore";
import { LiveProvider } from "../../live/LiveProvider";
import { MapStyleProvider } from "../../widgets/map/style";
import { startEmbedSession, type EmbedSession } from "../../embed/session";
import { DashboardWidget } from "./DashboardWidget";

export function EmbedPage({ dashboardId, widgetId }: { dashboardId: string; widgetId: string }) {
  const { t, i18n } = useTranslation();
  const [session] = useState<EmbedSession | undefined>(() => {
    const s = startEmbedSession();
    // Set before the first child renders, so the first request already carries X-Org.
    if (s) setCurrentOrgId(s.org);
    return s;
  });
  useEffect(() => {
    if (session?.lang && i18n.language !== session.lang) void i18n.changeLanguage(session.lang);
  }, [session, i18n]);

  if (!session)
    return (
      <main className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-800">
        {t("embed.appOnly")}
      </main>
    );
  return (
    <NoNavigation>
      <LiveProvider orgId={session.org}>
        <MapStyleProvider>
          <main className="h-full bg-white p-2">
            <DashboardWidget orgId={session.org} dashboardId={dashboardId} widgetId={widgetId} />
          </main>
        </MapStyleProvider>
      </LiveProvider>
    </NoNavigation>
  );
}

/** Stops every link inside from navigating (MD2-03: taps work as on the web, but no other page opens). */
export function NoNavigation({ children }: { children: ReactNode }) {
  const stop = (e: MouseEvent) => {
    if ((e.target as Element).closest?.("a[href]")) e.preventDefault();
  };
  return (
    <div className="h-full" onClickCapture={stop}>
      {children}
    </div>
  );
}
